/**
 * Shared wording for client-side missing-translation reports, and the pure
 * helpers the E2E `page` fixture (`e2e/fixtures.ts`) uses to turn those
 * reports into test failures.
 *
 * Dependency-free on purpose: the Playwright runner imports this module
 * outside the Next.js bundle, so it must not pull in Sentry or React.
 */

/** Prefix of every intl issue reported by `utils/nextIntl.ts`. */
export const INTL_ISSUE_PREFIX = "[i18n]";

export const MISSING_MESSAGE_CODE = "MISSING_MESSAGE";

/**
 * The message `utils/nextIntl.ts` reports for an intl issue, e.g.
 * `[i18n] MISSING_MESSAGE: dashboard.balance.title`. It is also the Sentry
 * issue title, so changing it regroups existing issues.
 */
export const formatIntlIssueMessage = (code: string, path: string): string =>
  `${INTL_ISSUE_PREFIX} ${code}: ${path}`;

export const MISSING_TRANSLATION_MESSAGE_PREFIX = formatIntlIssueMessage(
  MISSING_MESSAGE_CODE,
  ""
);

// reportSilentFailure (lib/reportSilentFailure.ts) writes
// `console.warn("[silent-failure] " + message, JSON.stringify({ tags, extra }))`.
// The unit test drives the real getMessageFallback through console.warn, so a
// change to that line fails there rather than silently disabling the E2E gate.
const MISSING_TRANSLATION_CONSOLE_PREFIX = `[silent-failure] ${MISSING_TRANSLATION_MESSAGE_PREFIX}`;

// The JSON argument reportSilentFailure appends after a space: `{}` when the
// report has no tags or extra, otherwise an object opening with a quoted key.
const TRAILING_JSON_ARGUMENT = /\s\{(?:\}|").*$/s;

/**
 * The translation key from a client console line reporting a missing message,
 * or null for any other line (other intl codes, other silent-failure reports,
 * unrelated output). The key ends at the JSON argument reportSilentFailure
 * appends, or at the end of the line when there is none; trailing whitespace
 * is trimmed. Spaces inside the key are kept, because message paths can have
 * space-bearing segments (`bankAccount.Faster payments.default`).
 */
export const parseMissingTranslationKey = (
  consoleText: string
): string | null => {
  if (!consoleText.startsWith(MISSING_TRANSLATION_CONSOLE_PREFIX)) return null;
  const key = consoleText
    .slice(MISSING_TRANSLATION_CONSOLE_PREFIX.length)
    .replace(TRAILING_JSON_ARGUMENT, "")
    .trimEnd();
  return key ? key : null;
};

/**
 * The key the test-build-only page app/[locale]/(public)/test-fixtures/
 * missing-translation renders and e2e/missing-translation-gate.spec.ts
 * declares, so both sides spell it once. Its namespace is deliberately absent
 * from messages/en-GB.json (never add it): the page must hit next-intl's real
 * missing-message path for the gate's positive controls to mean anything.
 */
export const MISSING_TRANSLATION_PROBE_KEY =
  "missingTranslationGateProbe.label";

/**
 * Compare the missing keys a test rendered with the keys it declared:
 * `undeclared` rendered without a declaration, `unseen` were declared but
 * never rendered (a stale declaration).
 */
export const missingTranslationViolations = (
  seen: Iterable<string>,
  declared: Iterable<string>
): { undeclared: string[]; unseen: string[] } => {
  const seenSet = new Set(seen);
  const declaredSet = new Set(declared);
  return {
    undeclared: [...seenSet].filter((key) => !declaredSet.has(key)),
    unseen: [...declaredSet].filter((key) => !seenSet.has(key)),
  };
};
